
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import StandardLayout from "@/layouts/StandardLayout";

interface RedirectHandlerProps {
  targetUrl?: string;
  reason?: string;
  redirectCode?: 301 | 302 | 307 | 308;
  delay?: number;
}

const RedirectHandler = ({ 
  targetUrl, 
  reason, 
  redirectCode = 302, 
  delay = 5 
}: RedirectHandlerProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState(delay);
  
  // If no targetUrl is provided, this component acts as a passive handler
  // and doesn't render anything - just returns null
  if (!targetUrl || !reason) {
    return null;
  }
  
  useEffect(() => {
    // Set the proper redirect HTTP status if possible
    // Note: Client-side redirects don't actually set HTTP status codes
    // This would need server support for true 3xx status codes
    document.title = `Redirigiendo | Punto Hispano`;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          
          // Check if it's an external URL
          if (targetUrl.startsWith('http')) {
            window.location.href = targetUrl;
          } else {
            navigate(targetUrl);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [targetUrl, navigate, delay]);
  
  return (
    <StandardLayout
      title="Redirigiendo..."
      description={`Será redirigido automáticamente en ${countdown} segundos.`}
      noindex={true}
      withPadding={false}
      showBreadcrumb={false}
    >
      <div className="min-h-[80vh] flex items-center justify-center relative">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        <motion.div 
          className="max-w-lg text-center px-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6 flex justify-center">
            <div className="w-16 h-16 rounded-full bg-punto/20 flex items-center justify-center border border-punto/30">
              <div className="relative h-6 w-6">
                <div className="absolute inset-0 h-full w-full rounded-full border-t-2 border-punto animate-spin"></div>
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold mb-4">Redirigiendo...</h1>
          
          <p className="text-xl mb-6 text-foreground/80">{reason}</p>
          
          <p className="mb-8">
            Será redirigido automáticamente en <span className="text-punto font-bold">{countdown}</span> segundos.
          </p>
          
          <p className="text-sm text-foreground/60">
            Si no es redirigido automáticamente, 
            <a 
              href={targetUrl} 
              className="text-punto hover:underline ml-1"
            >
              haga clic aquí
            </a>.
          </p>
          
          <div className="mt-8 w-full bg-background/30 h-2 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-punto"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: delay, ease: "linear" }}
            />
          </div>
          
          <p className="mt-4 text-xs text-foreground/50">
            Código de redirección HTTP: {redirectCode}
          </p>
        </motion.div>
      </div>
    </StandardLayout>
  );
};

export default RedirectHandler;
